"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpanSubFormTypeEditor = void 0;
const tslib_1 = require("tslib");
const core_shared_1 = require("@ikegps/core-shared");
const mithril_1 = require("../../../mithril");
const input_select_1 = require("../../../components/input/input.select");
const mobx_1 = require("mobx");
const i18n_1 = require("../../../i18n");
const mobx_2 = require("../../../state/mobx");
const UNSELECTED_VALUE = '__UNSELECTED__';
class SpanSubFormTypeEditor {
    constructor(field, stateGroup) {
        var _a, _b, _c;
        this.field = field;
        this.stateGroup = stateGroup;
        this.reactions = new mobx_2.MobxState();
        const spanState = this.field.subFormSpanTypeState;
        // 1. Create all three select states with current options and values
        this.vectorFieldSelectState = this.createVectorFieldSelect((_a = spanState === null || spanState === void 0 ? void 0 : spanState.vectorFieldId) !== null && _a !== void 0 ? _a : null);
        this.typeFieldSelectState = this.createTypeFieldSelect((_b = spanState === null || spanState === void 0 ? void 0 : spanState.typeFieldId) !== null && _b !== void 0 ? _b : null);
        this.midSpanPhotoFieldSelectState = this.createMidSpanPhotoFieldSelect((_c = spanState === null || spanState === void 0 ? void 0 : spanState.midSpanIkePhotoFieldId) !== null && _c !== void 0 ? _c : null);
        // 2. Set isRequired AFTER values are set, so the auto-select reaction
        //    in SelectInputState sees a truthy value and returns early
        this.vectorFieldSelectState.isRequired = true;
        this.typeFieldSelectState.isRequired = true;
        this.midSpanPhotoFieldSelectState.isRequired = true;
        // 3. Register field-change reactions that update options when subform fields change
        this.reactions.reaction(() => this.field.fields
            .filter(core_shared_1.isValidSpanVectorField)
            .map((f) => f.displayName)
            .join(','), () => this.updateVectorFieldOptions());
        this.reactions.reaction(() => this.field.fields
            .filter(core_shared_1.isValidSpanTypeField)
            .map((f) => f.displayName)
            .join(','), () => this.updateTypeFieldOptions());
        this.reactions.reaction(() => this.field.fields
            .filter(core_shared_1.isValidMidSpanIkePhotoField)
            .map((f) => f.displayName)
            .join(','), () => this.updateMidSpanPhotoFieldOptions());
        // 4. Register onValue handlers UNCONDITIONALLY
        //    for existing SPAN forms, these fire when the user changes a dropdown.
        this.vectorFieldSelectState.onValue((value) => {
            if (value) {
                this.field.Mutator.span.selectVectorField(value);
            }
        });
        this.typeFieldSelectState.onValue((value) => {
            if (value) {
                this.field.Mutator.span.selectSpanTypeField(value);
            }
        });
        this.midSpanPhotoFieldSelectState.onValue((value) => {
            if (value && value !== UNSELECTED_VALUE) {
                // Remove the unselected placeholder option when a real value is chosen
                const filteredOptions = this.midSpanPhotoFieldSelectState.options
                    .filter((opt) => opt.value !== UNSELECTED_VALUE)
                    .slice();
                this.midSpanPhotoFieldSelectState.setOptions(filteredOptions);
                this.midSpanPhotoFieldSelectState.setError(null);
                this.field.Mutator.span.selectMidSpanIkePhotoField(value);
            }
        });
    }
    createVectorFieldSelect(currentValue) {
        const options = this.field.fields
            .filter(core_shared_1.isValidSpanVectorField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        const state = new input_select_1.SelectInputState((0, i18n_1.Translate)(i18n_1.T.SPAN_VECTOR_FIELD), options);
        state.setValue(currentValue);
        this.stateGroup.add(state);
        state.isLabelShown = true;
        return state;
    }
    createTypeFieldSelect(currentValue) {
        const options = this.field.fields
            .filter(core_shared_1.isValidSpanTypeField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        const state = new input_select_1.SelectInputState((0, i18n_1.Translate)(i18n_1.T.SPAN_TYPE_FIELD), options);
        state.setValue(currentValue);
        this.stateGroup.add(state);
        state.isLabelShown = true;
        return state;
    }
    createMidSpanPhotoFieldSelect(currentValue) {
        const options = this.field.fields
            .filter(core_shared_1.isValidMidSpanIkePhotoField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        // Add unselected placeholder if this is an existing SPAN without the field set
        const needsUnselectedOption = !currentValue;
        if (needsUnselectedOption) {
            options.unshift({
                key: (0, i18n_1.Translate)(i18n_1.T.SELECT_OPTION),
                value: UNSELECTED_VALUE,
            });
        }
        const state = new input_select_1.SelectInputState((0, i18n_1.Translate)(i18n_1.T.SPAN_MID_SPAN_IKE_PHOTO_FIELD), options);
        if (currentValue) {
            state.setValue(currentValue);
        }
        else if (needsUnselectedOption) {
            state.setValue(UNSELECTED_VALUE);
            state.setError((0, i18n_1.Translate)(i18n_1.T.INPUT_REQUIRED));
            state.setTouched();
        }
        this.stateGroup.add(state);
        state.isLabelShown = true;
        return state;
    }
    updateVectorFieldOptions() {
        if (!this.vectorFieldSelectState)
            return;
        const options = this.field.fields
            .filter(core_shared_1.isValidSpanVectorField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        this.vectorFieldSelectState.setOptions(options);
        if (!options.find((opt) => opt.value === this.vectorFieldSelectState.value)) {
            this.vectorFieldSelectState.setValue(null);
        }
    }
    updateTypeFieldOptions() {
        if (!this.typeFieldSelectState)
            return;
        const options = this.field.fields
            .filter(core_shared_1.isValidSpanTypeField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        this.typeFieldSelectState.setOptions(options);
        if (!options.find((opt) => opt.value === this.typeFieldSelectState.value)) {
            this.typeFieldSelectState.setValue(null);
        }
    }
    updateMidSpanPhotoFieldOptions() {
        if (!this.midSpanPhotoFieldSelectState)
            return;
        const currentValue = this.midSpanPhotoFieldSelectState.value;
        const options = this.field.fields
            .filter(core_shared_1.isValidMidSpanIkePhotoField)
            .map((field) => ({ key: field.displayName, value: field.id }));
        // Preserve the UNSELECTED placeholder if it's the current value (fixes Bug 1)
        if (currentValue === UNSELECTED_VALUE) {
            options.unshift({
                key: (0, i18n_1.Translate)(i18n_1.T.SELECT_OPTION),
                value: UNSELECTED_VALUE,
            });
        }
        this.midSpanPhotoFieldSelectState.setOptions(options);
        if (!options.find((opt) => opt.value === currentValue)) {
            this.midSpanPhotoFieldSelectState.setValue(null);
        }
    }
    hasNoValidMidSpanPhotoOptions() {
        return this.field.fields.filter(core_shared_1.isValidMidSpanIkePhotoField).length === 0;
    }
    createMidSpanPhotoField() {
        const field = this.field.Mutator.span.addMidSpanPhotoField();
        const options = this.field.fields
            .filter(core_shared_1.isValidMidSpanIkePhotoField)
            .map((f) => ({ key: f.displayName, value: f.id }));
        this.midSpanPhotoFieldSelectState.setOptions(options);
        this.midSpanPhotoFieldSelectState.setValue(field.id);
        this.midSpanPhotoFieldSelectState.setError(null);
    }
    view() {
        return (0, mithril_1.m)('div', {}, [
            input_select_1.SelectInputComponent.view(this.vectorFieldSelectState),
            input_select_1.SelectInputComponent.view(this.typeFieldSelectState),
            (0, mithril_1.m)('div', { style: { display: 'flex', alignItems: 'center' } }, [
                input_select_1.SelectInputComponent.view(this.midSpanPhotoFieldSelectState),
                this.hasNoValidMidSpanPhotoOptions()
                    ? (0, mithril_1.m)('button', {
                        className: 'mdl-button',
                        style: { marginLeft: '16px' },
                        onclick: () => this.createMidSpanPhotoField(),
                    }, (0, i18n_1.Translate)(i18n_1.T.FORM_FIELD_CREATE))
                    : null,
            ]),
        ]);
    }
    destroy() {
        this.reactions.destroy();
        // Remove select states from the parent state group
        this.stateGroup.remove(this.vectorFieldSelectState);
        this.stateGroup.remove(this.typeFieldSelectState);
        this.stateGroup.remove(this.midSpanPhotoFieldSelectState);
    }
}
exports.SpanSubFormTypeEditor = SpanSubFormTypeEditor;
tslib_1.__decorate([
    mobx_1.action
], SpanSubFormTypeEditor.prototype, "updateVectorFieldOptions", null);
tslib_1.__decorate([
    mobx_1.action
], SpanSubFormTypeEditor.prototype, "updateTypeFieldOptions", null);
tslib_1.__decorate([
    mobx_1.action
], SpanSubFormTypeEditor.prototype, "updateMidSpanPhotoFieldOptions", null);
tslib_1.__decorate([
    mobx_1.action
], SpanSubFormTypeEditor.prototype, "createMidSpanPhotoField", null);
//# sourceMappingURL=form.field.editor.subform.span.js.map